import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "./adminApi";
import { ImageUpload } from "./ImageUpload";

const inp =
  "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-gold";

export const MassageForm = ({ massage, therapists, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    name: { pt: "", en: "", es: "", ...massage.name },
    description: { pt: "", en: "", es: "", ...massage.description },
    duration: massage.duration || "",
    cover: massage.cover || "",
    featured: !!massage.featured,
    order: massage.order ?? 99,
    therapists: massage.therapists || [],
    badge: { pt: "", en: "", es: "", ...massage.badge },
    active: massage.active !== false,
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMl = (field, lang, v) => setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: v } }));
  const toggleTherapist = (slug) =>
    set("therapists", form.therapists.includes(slug) ? form.therapists.filter((s) => s !== slug) : [...form.therapists, slug]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/massages/${massage.slug}`, { ...form, order: Number(form.order) || 99 });
      toast.success("Massagem guardada com sucesso");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="massage-form"
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#101010] text-white"
      >
        <DialogTitle className="font-display text-2xl font-medium">Editar {massage.name?.pt}</DialogTitle>
        <form onSubmit={save} className="space-y-5">
          {["pt", "en", "es"].map((lang) => (
            <div key={lang} className="grid grid-cols-3 gap-4">
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Nome ({lang.toUpperCase()})</label>
                <input data-testid={`massage-name-${lang}`} className={inp} value={form.name[lang]} onChange={(e) => setMl("name", lang, e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Descrição ({lang.toUpperCase()})</label>
                <textarea data-testid={`massage-desc-${lang}`} rows={2} className={inp} value={form.description[lang]} onChange={(e) => setMl("description", lang, e.target.value)} />
              </div>
            </div>
          ))}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Etiqueta (PT)</label>
              <input data-testid="massage-badge-pt" className={inp} value={form.badge.pt} onChange={(e) => setMl("badge", "pt", e.target.value)} placeholder="Ex.: Novidade" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Etiqueta (EN)</label>
              <input data-testid="massage-badge-en" className={inp} value={form.badge.en} onChange={(e) => setMl("badge", "en", e.target.value)} placeholder="E.g.: New" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Etiqueta (ES)</label>
              <input data-testid="massage-badge-es" className={inp} value={form.badge.es} onChange={(e) => setMl("badge", "es", e.target.value)} placeholder="Ej.: Novedad" />
            </div>
          </div>
          <p className="-mt-3 text-[11px] text-white/30">Etiqueta dourada mostrada no cartão da massagem no site. Deixe vazio para não mostrar.</p>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Duração</label>
              <input data-testid="massage-duration-input" className={inp} value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="30/60 min" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Ordem</label>
              <input data-testid="massage-order-input" type="number" className={inp} value={form.order} onChange={(e) => set("order", e.target.value)} />
            </div>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
                <input
                  data-testid="massage-featured-checkbox"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => set("featured", e.target.checked)}
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                Destaque na homepage
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
                <input
                  data-testid="massage-active-checkbox"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set("active", e.target.checked)}
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                Ativa (visível no site)
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Terapeutas</label>
            <div className="flex flex-wrap gap-2">
              {therapists.map((th) => (
                <button
                  key={th.slug}
                  type="button"
                  data-testid={`massage-therapist-toggle-${th.slug}`}
                  onClick={() => toggleTherapist(th.slug)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    form.therapists.includes(th.slug)
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                >
                  {th.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">Imagem de Capa</label>
            <ImageUpload value={form.cover} onChange={(url) => set("cover", url)} testId="massage-cover-upload" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-white/15 px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white">
              Cancelar
            </button>
            <button
              data-testid="massage-save-button"
              type="submit"
              disabled={busy}
              className="flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-light disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
